/**
 * Shared utilities for RoofSafety anchor point and access point visualization.
 * Used by: RoofSafetyAnchorPointsModal, ComponentDetailsView, HistoryFieldRenderers
 */
import { AnchorPointSafetyStatus, BlueprintLocation, PhotoModel } from '../types/protocols';

// ── Color Constants ───────────────────────────────────────────────────

/**
 * Color palette for anchor point safety status visualization.
 * Bootstrap-inspired colors for consistency with the rest of the UI.
 */
export const ANCHOR_POINT_COLORS: Record<AnchorPointSafetyStatus, string> = {
    [AnchorPointSafetyStatus.SAFE]: '#28a745',        // Bootstrap success green
    [AnchorPointSafetyStatus.NOT_SAFE]: '#dc3545',    // Bootstrap danger red
    [AnchorPointSafetyStatus.UNDETERMINED]: '#fd7e14' // Bootstrap warning orange
};

/** Blue color for access point markers */
export const ACCESS_POINT_COLOR = '#0d6efd'; // Bootstrap primary blue

// ── Color Functions ───────────────────────────────────────────────────

/**
 * Returns the display color for an anchor point based on its safety status.
 * Used for map markers, legend indicators, and list item accents.
 */
export function getAnchorPointStatusColor(status: AnchorPointSafetyStatus): string {
    return ANCHOR_POINT_COLORS[status] ?? ANCHOR_POINT_COLORS[AnchorPointSafetyStatus.UNDETERMINED];
}

/**
 * Returns the display color for an anchor point, accepting string for flexibility.
 * Useful when parsing JSON data where the type may not be strictly enforced.
 */
export function getAnchorPointColorFromString(status: string | undefined): string {
    if (status === AnchorPointSafetyStatus.SAFE) return ANCHOR_POINT_COLORS[AnchorPointSafetyStatus.SAFE];
    if (status === AnchorPointSafetyStatus.NOT_SAFE) return ANCHOR_POINT_COLORS[AnchorPointSafetyStatus.NOT_SAFE];
    return ANCHOR_POINT_COLORS[AnchorPointSafetyStatus.UNDETERMINED];
}

// ── Position Scaling ──────────────────────────────────────────────────

export interface ContainerDimensions {
    width: number;
    height: number;
}

export interface ScaledPosition {
    x: number;
    y: number;
}

/**
 * Scales a blueprint location to screen coordinates based on container size.
 *
 * The scaling logic handles two scenarios:
 * 1. If the location includes width/height (from when it was placed), use those
 *    to calculate the scale factor for accurate positioning.
 * 2. Otherwise, fall back to the image dimensions for a proportional scale.
 *
 * @param position - The blueprint location with x, y coordinates
 * @param containerDimensions - The current container size on screen
 * @param mapImage - The map image with width/height dimensions
 * @returns Scaled x, y coordinates or null if dimensions are invalid
 */
export function getScaledPosition(
    position: BlueprintLocation,
    containerDimensions: ContainerDimensions,
    mapImage: PhotoModel | null
): ScaledPosition | null {
    if (!mapImage || !containerDimensions.width || !containerDimensions.height) {
        return null;
    }

    const { x, y, width: locWidth, height: locHeight } = position;
    const { width: imgWidth, height: imgHeight } = mapImage;

    if (!imgWidth || !imgHeight) {
        return null;
    }

    let scaledX: number;
    let scaledY: number;

    // Use stored location dimensions if available (more accurate)
    if (locWidth && locHeight && locWidth > 1 && locHeight > 1) {
        const scale = containerDimensions.width / locWidth;
        scaledX = x * scale;
        scaledY = y * scale;
    } else {
        // Fall back to image dimensions
        const scaleX = containerDimensions.width / imgWidth;
        const scaleY = containerDimensions.height / imgHeight;
        scaledX = x * scaleX;
        scaledY = y * scaleY;
    }

    return { x: scaledX, y: scaledY };
}

/**
 * Scales a blueprint location for a container using object-fit: contain.
 * This accounts for letterboxing where the image doesn't fill the entire container.
 *
 * @param position - The blueprint location with x, y coordinates
 * @param containerDimensions - The container size on screen
 * @param mapImage - The map image with original width/height
 * @returns Scaled x, y coordinates relative to container, or null if invalid
 */
export function getScaledPositionWithContain(
    position: BlueprintLocation,
    containerDimensions: ContainerDimensions,
    mapImage: PhotoModel | null
): ScaledPosition | null {
    if (!mapImage || !containerDimensions.width || !containerDimensions.height) {
        return null;
    }

    const { x, y, width: locWidth, height: locHeight } = position;
    const { width: imgWidth, height: imgHeight } = mapImage;

    if (!imgWidth || !imgHeight) {
        return null;
    }

    // Calculate how the image is rendered with object-fit: contain
    const containerAspect = containerDimensions.width / containerDimensions.height;
    const imageAspect = imgWidth / imgHeight;

    let renderedWidth: number;
    let renderedHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (imageAspect > containerAspect) {
        // Image is wider than container - letterbox top/bottom
        renderedWidth = containerDimensions.width;
        renderedHeight = containerDimensions.width / imageAspect;
        offsetX = 0;
        offsetY = (containerDimensions.height - renderedHeight) / 2;
    } else {
        // Image is taller than container - letterbox left/right
        renderedHeight = containerDimensions.height;
        renderedWidth = containerDimensions.height * imageAspect;
        offsetX = (containerDimensions.width - renderedWidth) / 2;
        offsetY = 0;
    }

    let scaledX: number;
    let scaledY: number;

    // Use stored location dimensions if available (more accurate)
    if (locWidth && locHeight && locWidth > 1 && locHeight > 1) {
        const scale = renderedWidth / locWidth;
        scaledX = x * scale + offsetX;
        scaledY = y * scale + offsetY;
    } else {
        // Scale relative to original image dimensions
        scaledX = (x / imgWidth) * renderedWidth + offsetX;
        scaledY = (y / imgHeight) * renderedHeight + offsetY;
    }

    return { x: scaledX, y: scaledY };
}

/**
 * Scales a position for a fixed-size preview container (e.g., history view).
 * Uses only width-based scaling since the container has a fixed size.
 */
export function getScaledPositionForFixedContainer(
    position: BlueprintLocation,
    containerSize: number,
    mapImage: PhotoModel | null
): ScaledPosition | null {
    if (!mapImage) {
        return null;
    }

    const { x, y, width: locWidth, height: locHeight } = position;
    const { width: imgWidth, height: imgHeight } = mapImage;

    if (!imgWidth || !imgHeight) {
        return null;
    }

    let scaledX: number;
    let scaledY: number;

    if (locWidth && locHeight && locWidth > 1 && locHeight > 1) {
        const scale = containerSize / locWidth;
        scaledX = x * scale;
        scaledY = y * scale;
    } else {
        scaledX = (x / imgWidth) * containerSize;
        scaledY = (y / imgHeight) * containerSize;
    }

    return { x: scaledX, y: scaledY };
}
